import { useMemo } from "react";
import { useOutletContext, useParams } from "react-router";

import Box from "ds/components/Box";
import PageLoading from "components/loading/PageLoading";
import InfiniteScroll from "components/scroll/InfiniteScroll";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import { TemplateDeployment } from "types/generated";

import useTemplateDeploymentChangeHistory from "./useChangeHistory";
import TemplateDeploymentChangeHistoryEmpty from "./Empty";
import ChangeHistoryDateGroup from "./ChangeHistoryDateGroup";
import { getLatestEntryId } from "./helpers";
import styles from "./styles.module.css";

type TemplateDeploymentChangeHistoryOutlet = {
  deployment: TemplateDeployment;
};

const TemplateDeploymentChangeHistory = () => {
  const { deployment } = useOutletContext<TemplateDeploymentChangeHistoryOutlet>();
  const { templateId } = useParams<{ templateId: string }>();

  const { dateGroups, loading, initialLoading, hasNextPage, loadMore, error } =
    useTemplateDeploymentChangeHistory({
      templateId: templateId!,
      deploymentId: deployment.id,
    });

  const latestEntryId = useMemo(() => getLatestEntryId(dateGroups), [dateGroups]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return <PageLoading />;
  }

  if (dateGroups.length === 0) {
    return <TemplateDeploymentChangeHistoryEmpty />;
  }

  return (
    <>
      <PageInfoHeader title="Change history" />
      <div className={styles.scrollArea}>
        <InfiniteScroll onScrollEnd={loadMore} hasMore={hasNextPage} loading={loading}>
          <Box direction="column" gap="x-large" padding="x-large" relative>
            {dateGroups.map((group) => (
              <ChangeHistoryDateGroup
                key={group.dateKey}
                group={group}
                templateId={templateId!}
                deploymentId={deployment.id}
                currentInputs={deployment.inputs ?? []}
                currentVersionNumber={deployment.templateVersion?.version ?? ""}
                latestEntryId={latestEntryId}
              />
            ))}
          </Box>
        </InfiniteScroll>
      </div>
    </>
  );
};

export default TemplateDeploymentChangeHistory;
